export type ServiceProfile = 'payment' | 'sale' | 'range' | 'expense';
export type ServiceAmountMode = 'fixed' | 'manual' | 'range';

export interface ServiceCapabilities {
    service_profile: ServiceProfile;
    requires_account: boolean;
    has_commission: boolean;
    amount_mode: ServiceAmountMode;
    track_cost: boolean;
    outflow: boolean;
}

export const SERVICE_PROFILE_OPTIONS: Array<{value: ServiceProfile; label: string; hint: string}> = [
    {
        value: 'payment',
        label: 'Платёж / перевод',
        hint: 'Счёт клиента, сумма вручную, комиссия',
    },
    {
        value: 'sale',
        label: 'Продажа услуги',
        hint: 'Фиксированная цена и себестоимость / маржа',
    },
    {
        value: 'range',
        label: 'Сумма в диапазоне',
        hint: 'Клиент вводит сумму в пределах min–max',
    },
    {
        value: 'expense',
        label: 'Расход',
        hint: 'Исходящая операция, сумма вручную',
    },
];

export const SERVICE_AMOUNT_MODE_OPTIONS: Array<{value: ServiceAmountMode; label: string}> = [
    {value: 'manual', label: 'Вручную'},
    {value: 'fixed', label: 'Фиксированная цена'},
    {value: 'range', label: 'Диапазон min–max'},
];

export const SERVICE_PROFILE_PRESETS: Record<ServiceProfile, ServiceCapabilities> = {
    payment: {
        service_profile: 'payment',
        requires_account: true,
        has_commission: true,
        amount_mode: 'manual',
        track_cost: false,
        outflow: false,
    },
    sale: {
        service_profile: 'sale',
        requires_account: false,
        has_commission: false,
        amount_mode: 'fixed',
        track_cost: true,
        outflow: false,
    },
    range: {
        service_profile: 'range',
        requires_account: false,
        has_commission: false,
        amount_mode: 'range',
        track_cost: false,
        outflow: false,
    },
    expense: {
        service_profile: 'expense',
        requires_account: false,
        has_commission: false,
        amount_mode: 'manual',
        track_cost: false,
        outflow: true,
    },
};

const asProfile = (value: unknown): ServiceProfile => {
    if (value === 'sale' || value === 'range' || value === 'expense' || value === 'payment') {
        return value;
    }
    return 'payment';
};

const asAmountMode = (value: unknown): ServiceAmountMode => {
    if (value === 'fixed' || value === 'range' || value === 'manual') {
        return value;
    }
    return 'manual';
};

const formOutflow = (form: Record<string, any>): boolean =>
    Number(form.outflow_id) === 2 || form.outflow === true;

/** Exact preset match by capability flags (not by profile label). */
export const findMatchingServiceProfile = (
    caps: Partial<ServiceCapabilities> & {outflow?: boolean},
): ServiceProfile | null => {
    const amountMode = asAmountMode(caps.amount_mode);
    const requiresAccount = Boolean(caps.requires_account);
    const hasCommission = Boolean(caps.has_commission);
    const trackCost = Boolean(caps.track_cost);
    const outflow = Boolean(caps.outflow);

    for (const profile of Object.keys(SERVICE_PROFILE_PRESETS) as ServiceProfile[]) {
        const preset = SERVICE_PROFILE_PRESETS[profile];
        if (
            preset.requires_account === requiresAccount
            && preset.has_commission === hasCommission
            && preset.amount_mode === amountMode
            && preset.track_cost === trackCost
            && preset.outflow === outflow
        ) {
            return profile;
        }
    }
    return null;
};

export const getServiceProfileHint = (profile?: string | null): string => {
    const key = asProfile(profile);
    return SERVICE_PROFILE_OPTIONS.find((option) => option.value === key)?.hint || '';
};

/** Legacy-safe defaults: missing fields behave like payment service. */
export const resolveServiceCapabilities = (source?: Partial<ServiceCapabilities> | Record<string, any> | null): ServiceCapabilities => {
    if (!source) {
        return {...SERVICE_PROFILE_PRESETS.payment};
    }

    const hasExplicitProfile = source.service_profile != null && String(source.service_profile).length > 0;
    const hasExplicitAccount = typeof source.requires_account === 'boolean';
    const hasExplicitCommission = typeof source.has_commission === 'boolean';
    const hasExplicitMode = source.amount_mode != null && String(source.amount_mode).length > 0;
    const hasExplicitTrackCost = typeof source.track_cost === 'boolean';

    // Fully legacy payload → payment defaults
    if (!hasExplicitProfile && !hasExplicitAccount && !hasExplicitCommission && !hasExplicitMode && !hasExplicitTrackCost) {
        return {
            ...SERVICE_PROFILE_PRESETS.payment,
            outflow: Boolean(source.outflow),
        };
    }

    const profile = asProfile(source.service_profile);
    const preset = SERVICE_PROFILE_PRESETS[profile];

    return {
        service_profile: profile,
        requires_account: hasExplicitAccount ? Boolean(source.requires_account) : preset.requires_account,
        has_commission: hasExplicitCommission ? Boolean(source.has_commission) : preset.has_commission,
        amount_mode: hasExplicitMode ? asAmountMode(source.amount_mode) : preset.amount_mode,
        track_cost: hasExplicitTrackCost ? Boolean(source.track_cost) : preset.track_cost,
        outflow: typeof source.outflow === 'boolean' ? source.outflow : preset.outflow,
    };
};

export const applyServiceProfilePreset = <T extends Record<string, any>>(form: T, profile: ServiceProfile): T => {
    const preset = SERVICE_PROFILE_PRESETS[profile] || SERVICE_PROFILE_PRESETS.payment;
    return {
        ...form,
        service_profile: preset.service_profile,
        requires_account: preset.requires_account,
        has_commission: preset.has_commission,
        amount_mode: preset.amount_mode,
        track_cost: preset.track_cost,
        outflow: preset.outflow,
        outflow_id: preset.outflow ? 2 : 1,
    };
};

/**
 * Keep profile label / outflow consistent when user edits capability switches or Приход/Расход.
 * - Exact flag match → rename profile to that preset
 * - outflow=расход while not expense → switch to expense preset
 * - outflow=приход while expense → switch to payment preset
 */
export const reconcileServiceFormCapabilities = <T extends Record<string, any>>(
    prev: T,
    next: T,
): T => {
    if (Number(next.type_id) !== 2) {
        return next;
    }

    const prevProfile = asProfile(prev.service_profile);
    const nextProfile = asProfile(next.service_profile);
    if (nextProfile !== prevProfile) {
        return applyServiceProfilePreset(next, nextProfile);
    }

    const prevOutflow = formOutflow(prev);
    const nextOutflow = formOutflow(next);
    if (nextOutflow !== prevOutflow) {
        if (nextOutflow) {
            return applyServiceProfilePreset(next, 'expense');
        }
        if (prevProfile === 'expense') {
            return applyServiceProfilePreset(next, 'payment');
        }
    }

    const matched = findMatchingServiceProfile({
        requires_account: next.requires_account,
        has_commission: next.has_commission,
        amount_mode: asAmountMode(next.amount_mode),
        track_cost: next.track_cost,
        outflow: nextOutflow,
    });

    let reconciled: T = {
        ...next,
        outflow: nextOutflow,
        outflow_id: nextOutflow ? 2 : 1,
    };

    if (matched && matched !== nextProfile) {
        reconciled = {
            ...reconciled,
            service_profile: matched,
        };
    }

    // Expense profile always keeps исходящая операция
    if (asProfile(reconciled.service_profile) === 'expense' && !formOutflow(reconciled)) {
        reconciled = {
            ...reconciled,
            outflow: true,
            outflow_id: 2,
        };
    }

    return reconciled;
};

/** Strip unused commission / cost / price fields before persist. */
export const sanitizeServiceFormForSave = <T extends Record<string, any>>(form: T): T => {
    if (Number(form.type_id) !== 2) {
        return form;
    }

    const caps = resolveServiceCapabilities(form);
    const next: Record<string, any> = {
        ...form,
        service_profile: caps.service_profile,
        requires_account: caps.requires_account,
        has_commission: caps.has_commission,
        amount_mode: caps.amount_mode,
        track_cost: caps.track_cost,
        outflow: formOutflow(form) || caps.outflow,
        outflow_id: (formOutflow(form) || caps.outflow) ? 2 : 1,
    };

    if (!caps.has_commission) {
        next.commission_type_id = 0;
        next.commission = 0;
        next.fixed_commission = 0;
        next.commission_limit = 0;
        next.commission_before_limit = 0;
        next.commission_after_limit = 0;
    }

    if (!caps.track_cost) {
        next.cost = 0;
    }

    if (caps.amount_mode === 'fixed') {
        next.min = 0;
        next.max = 0;
    }

    return next as T;
};

export const serviceRequiresAccount = (source?: Record<string, any> | null): boolean =>
    resolveServiceCapabilities(source).requires_account;

export const serviceHasCommission = (source?: Record<string, any> | null, ownerCommissionsEnabled?: boolean): boolean => {
    if (!ownerCommissionsEnabled) return false;
    return resolveServiceCapabilities(source).has_commission;
};

export const serviceUsesAmountRange = (source?: Record<string, any> | null, ownerMinMaxEnabled?: boolean): boolean => {
    const caps = resolveServiceCapabilities(source);
    if (caps.amount_mode === 'fixed') return false;
    if (caps.amount_mode === 'range') return true;
    if (!ownerMinMaxEnabled) return false;
    // Legacy min/max without amount_mode
    return Number(source?.min) > 0 || Number(source?.max) > 0;
};

export const serviceUsesFixedAmount = (source?: Record<string, any> | null): boolean =>
    resolveServiceCapabilities(source).amount_mode === 'fixed';

/** Min/max в операции: не для фиксированной цены. */
export const serviceShouldEnforceMinMax = (
    source?: Record<string, any> | null,
    ownerMinMaxEnabled?: boolean,
    appliesOwnerMinMax?: boolean,
): boolean => {
    if (serviceUsesFixedAmount(source)) return false;
    return serviceUsesAmountRange(source, ownerMinMaxEnabled) || Boolean(appliesOwnerMinMax);
};
